import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import apiClient from '@/utils/axios.js';

/**
 * Per-user help / walkthrough progress (welcome prompt + tour status map).
 */
export const useHelpStore = defineStore('helpStore', () => {
    const loaded = ref(false);
    const loading = ref(false);
    const welcomePromptDismissed = ref(false);
    const tours = ref({});
    /** Session-only: hide welcome prompt until next full page load without persisting. */
    const welcomeDeferredThisSession = ref(false);

    const defaultProgress = () => ({
        welcome_prompt_dismissed: false,
        tours: {},
    });

    const applyData = (data) => {
        const payload = data ?? defaultProgress();
        welcomePromptDismissed.value = !!payload.welcome_prompt_dismissed;
        tours.value = payload.tours && typeof payload.tours === 'object' ? { ...payload.tours } : {};
    };

    const load = async (force = false) => {
        if (loaded.value && !force) {
            return;
        }
        if (loading.value) {
            return;
        }

        loading.value = true;
        try {
            const response = await apiClient.get('user/help-progress', { skipProgress: true });
            applyData(response.data?.data);
            loaded.value = true;
        } catch {
            if (!loaded.value) {
                applyData(defaultProgress());
            }
        } finally {
            loading.value = false;
        }
    };

    const persist = async (payload) => {
        const response = await apiClient.put('user/help-progress', payload, { skipProgress: true });
        applyData(response.data?.data);
        loaded.value = true;
        return response.data?.data;
    };

    const dismissWelcome = async (permanent = true) => {
        if (!permanent) {
            welcomeDeferredThisSession.value = true;
            return;
        }

        welcomeDeferredThisSession.value = true;
        welcomePromptDismissed.value = true;
        try {
            await persist({ welcome_prompt_dismissed: true });
        } catch {
            /* keep local dismiss; server may catch up later */
        }
    };

    const updateTourProgress = async (tourId, tourProgress) => {
        const previous = tours.value[tourId] ?? {};
        let completedAt;
        if (Object.prototype.hasOwnProperty.call(tourProgress, 'completed_at')) {
            completedAt = tourProgress.completed_at;
        } else if (tourProgress.status === 'completed') {
            completedAt = previous.completed_at ?? new Date().toISOString();
        } else {
            completedAt = previous.completed_at ?? null;
        }

        const next = {
            status: tourProgress.status,
            step_index: tourProgress.step_index ?? previous.step_index ?? 0,
            completed_at: completedAt,
        };

        tours.value = { ...tours.value, [tourId]: next };

        try {
            await persist({
                tour_id: tourId,
                tour: next,
            });
        } catch {
            /* local state already updated */
        }
    };

    const getTourProgress = (tourId) => {
        return (
            tours.value[tourId] ?? {
                status: 'not_started',
                step_index: 0,
                completed_at: null,
            }
        );
    };

    const shouldShowWelcome = computed(() => {
        return loaded.value && !welcomePromptDismissed.value && !welcomeDeferredThisSession.value;
    });

    const resetLocal = () => {
        loaded.value = false;
        loading.value = false;
        welcomePromptDismissed.value = false;
        tours.value = {};
        welcomeDeferredThisSession.value = false;
    };

    return {
        loaded,
        loading,
        welcomePromptDismissed,
        tours,
        welcomeDeferredThisSession,
        shouldShowWelcome,
        load,
        dismissWelcome,
        updateTourProgress,
        getTourProgress,
        resetLocal,
    };
});
